import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { discussionService } from '../services/api';
import { useAuth } from '../context/AuthContext';
// templates deprecated
import { addHoursToLocalInput, formatLocalRange, formatUtcRange, fromLocalInputValue, getDefaultValidityWindow } from '../utils/discussionTime';
import DiscussionAreaEditor from '../components/DiscussionAreaEditor';

const HAZARD_OPTIONS = ['tornado', 'hail', 'wind', 'flood', 'winter'];
const HIGHLIGHT_COLOR_PRESETS = ['#ff5a36', '#ffb347', '#2ec4b6', '#2d7ff9', '#8b5cf6'];
type DiscussionStatus = 'draft' | 'in_review' | 'ready' | 'published' | 'archived';

const ComposeDiscussionPage: React.FC = () => {
  const defaultValidityWindow = useMemo(() => getDefaultValidityWindow(), []);
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const canCompose = !!user?.permissions?.includes('create_discussions');
  const isRobot = user?.role === 'robot';
  // templates deprecated; no templates state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [title, setTitle] = useState('');
  const [concerningText, setConcerningText] = useState('');
  const [summary, setSummary] = useState('');
  const [discussionBody, setDiscussionBody] = useState('');
  const [status, setStatus] = useState<DiscussionStatus>(user?.role === 'robot' ? 'in_review' : 'published');
  const [tags, setTags] = useState('');
  const [hazardTags, setHazardTags] = useState<string[]>([]);
  const [tornadoProbability, setTornadoProbability] = useState('');
  const [windProbability, setWindProbability] = useState('');
  const [hailProbability, setHailProbability] = useState('');
  const [areaGeoJson, setAreaGeoJson] = useState('');
  const [areaWarnings, setAreaWarnings] = useState<string[]>([]);
  const [areaStates, setAreaStates] = useState<string[]>([]);

  // Check the drawn area against the five GLSN states (advisory only; never blocks saving).
  useEffect(() => {
    const value = areaGeoJson.trim();
    if (!value) {
      setAreaWarnings([]);
      setAreaStates([]);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      discussionService.areaCheck(value)
        .then((res) => {
          setAreaWarnings(res.data.warnings);
          setAreaStates(res.data.states);
        })
        .catch(() => {
          setAreaWarnings([]);
          setAreaStates([]);
        });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [areaGeoJson]);
  const [highlightColor, setHighlightColor] = useState('#ff5a36');
  const [useDefaultArchiveWindow, setUseDefaultArchiveWindow] = useState(true);
  const [validFromLocal, setValidFromLocal] = useState(defaultValidityWindow.validFromLocal);
  const [validToLocal, setValidToLocal] = useState(defaultValidityWindow.validToLocal);

  // templates removed — using official internal MD template

  const handleValidFromChange = (value: string) => {
    setValidFromLocal(value);
    if (useDefaultArchiveWindow) {
      setValidToLocal(addHoursToLocalInput(value, 3));
    }
  };

  const toggleHazard = (hazard: string) => {
    setHazardTags((current) =>
      current.includes(hazard) ? current.filter((item) => item !== hazard) : [...current, hazard],
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const response = await discussionService.create({
        title,
        concerning_text: concerningText.trim() || null,
        summary: summary.trim() || null,
        discussion_body: discussionBody.trim() || null,
        status: isRobot ? 'in_review' : status,
        tags: tags.split(',').map((item) => item.trim()).filter(Boolean),
        hazard_tags: hazardTags,
        tornado_probability: tornadoProbability.trim() || null,
        wind_probability: windProbability.trim() || null,
        hail_probability: hailProbability.trim() || null,
        area_geojson: areaGeoJson.trim() || null,
        highlight_color: highlightColor,
        valid_from_utc: fromLocalInputValue(validFromLocal),
        valid_to_utc: fromLocalInputValue(validToLocal),
      });
      navigate(`/discussions/${response.data.permalink}`);
    } catch (requestError: any) {
      setError(requestError.response?.data?.error || 'Failed to create discussion');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="page-shell">
        <div className="container detail-container">
          <div className="card auth-panel">
            <p className="eyebrow">Compose</p>
            <h1 className="page-heading">Sign in required</h1>
            <p className="page-copy">You need to sign in before you can create or edit GLSN discussions.</p>
            <div className="hero-actions">
              <Link to="/login" className="btn btn-primary">Go to login</Link>
              <Link to="/" className="btn btn-secondary">Return home</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!canCompose) {
    return (
      <div className="page-shell">
        <div className="container detail-container">
          <div className="card auth-panel">
            <p className="eyebrow">Compose</p>
            <h1 className="page-heading">Compose access restricted</h1>
            <p className="page-copy">Your role does not have the create-discussions capability. Ask an owner to update your role permissions if this is unexpected.</p>
            <div className="hero-actions">
              <Link to="/" className="btn btn-primary">Return home</Link>
              <Link to="/archive" className="btn btn-secondary">Browse archive</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div className="container">
        <div className="page-intro">
          <div>
            <p className="eyebrow">Create</p>
            <h1 className="page-heading">Create an internal discussion</h1>
            <p className="page-copy">
              {isRobot ? 'Create a discussion for staff review and set its MD validity window.' : 'Draft or publish a new product, assign hazard tags, and set the MD validity window that readers will see in Z and in their device time zone.'}
            </p>
          </div>
        </div>

        <div className="card compose-card">
          {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={handleSubmit} className="compose-form">
              <div className="form-grid two-column">
                {/* template selection removed — using official internal MD template */}

                <div className="form-group">
                  <label className="form-label" htmlFor="title">Headline</label>
                  <input id="title" className="form-input" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="GLSN Discussion #001" required />
                </div>
              </div>

              <div className="form-grid two-column">
                {!isRobot && <div className="form-group">
                  <label className="form-label" htmlFor="status">Status</label>
                  <select id="status" className="form-input" value={status} onChange={(event) => setStatus(event.target.value as DiscussionStatus)}>
                    <option value="draft">Draft</option>
                    <option value="in_review">In review</option>
                    <option value="ready">Ready to publish</option>
                    <option value="published">Published</option>
                  </select>
                </div>}

                {isRobot && <div className="form-group">
                  <span className="form-label">Status</span>
                  <p className="field-help">In review. A staff member must approve this discussion before publication.</p>
                </div>}

                <div className="form-group">
                  <p className="field-help">Using official GLSN MD template; no template selection required.</p>
                </div>
              </div>

              <div className="form-grid two-column">
                <div className="form-group">
                  <label className="form-label" htmlFor="valid-from">Valid from</label>
                  <input
                    id="valid-from"
                    type="datetime-local"
                    className="form-input"
                    value={validFromLocal}
                    onChange={(event) => handleValidFromChange(event.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="valid-to">Valid to</label>
                  <input
                    id="valid-to"
                    type="datetime-local"
                    className="form-input"
                    value={validToLocal}
                    onChange={(event) => setValidToLocal(event.target.value)}
                    disabled={useDefaultArchiveWindow}
                    required
                  />
                </div>
              </div>

              <label className="toggle-row-boxed">
                <input
                  type="checkbox"
                  checked={useDefaultArchiveWindow}
                  onChange={(event) => {
                    const nextValue = event.target.checked;
                    setUseDefaultArchiveWindow(nextValue);
                    if (nextValue) {
                      setValidToLocal(addHoursToLocalInput(validFromLocal, 3));
                    }
                  }}
                />
                <span>Auto-archive after 3 hours unless staff choose a custom end time.</span>
              </label>

              <p className="field-help">MD valid in Z: {formatUtcRange(fromLocalInputValue(validFromLocal), fromLocalInputValue(validToLocal)) || 'Not set yet'}</p>
              <p className="field-help">Device local time: {formatLocalRange(fromLocalInputValue(validFromLocal), fromLocalInputValue(validToLocal)) || 'Not set yet'}</p>

              <div className="form-group">
                <label className="form-label">Most Probable Peak Intensity</label>
                <p className="field-help">Use range-style values like 100-130 MPH, 65-80 MPH, or 1.00-1.75 in.</p>
              </div>

              <div className="form-grid two-column">
                <div className="form-group">
                  <label className="form-label" htmlFor="tornado-probability">Tornado</label>
                  <input
                    id="tornado-probability"
                    className="form-input"
                    value={tornadoProbability}
                    onChange={(event) => setTornadoProbability(event.target.value)}
                    placeholder="100-130 MPH"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="wind-probability">Wind</label>
                  <input
                    id="wind-probability"
                    className="form-input"
                    value={windProbability}
                    onChange={(event) => setWindProbability(event.target.value)}
                    placeholder="65-80 MPH"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="hail-probability">Hail</label>
                <input
                  id="hail-probability"
                  className="form-input"
                  value={hailProbability}
                  onChange={(event) => setHailProbability(event.target.value)}
                  placeholder="1.00-1.75 in."
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="concerning-text">Concerning</label>
                <textarea
                  id="concerning-text"
                  className="form-input form-textarea"
                  value={concerningText}
                  onChange={(event) => setConcerningText(event.target.value)}
                  placeholder="Concerning severe thunderstorms with damaging wind and isolated tornado potential"
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="summary">SUMMARY</label>
                <textarea id="summary" className="form-input form-textarea" value={summary} onChange={(event) => setSummary(event.target.value)} required />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="discussion-body">DISCUSSION</label>
                <textarea id="discussion-body" className="form-input form-textarea" value={discussionBody} onChange={(event) => setDiscussionBody(event.target.value)} required />
              </div>

              <div className="form-group">
                <label className="form-label">Area of concern</label>
                <div className="highlight-color-stack">
                  <div className="highlight-color-toolbar">
                    <label className="form-label" htmlFor="highlight-color">Highlight color</label>
                    <input
                      id="highlight-color"
                      type="color"
                      className="highlight-color-input"
                      value={highlightColor}
                      onChange={(event) => setHighlightColor(event.target.value)}
                    />
                  </div>
                  <div className="highlight-color-swatches">
                    {HIGHLIGHT_COLOR_PRESETS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={`highlight-swatch ${highlightColor === color ? 'highlight-swatch-active' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setHighlightColor(color)}
                        aria-label={`Use highlight color ${color}`}
                      />
                    ))}
                  </div>
                  <p className="field-help">This color is used on the map and in the generated public discussion graphic.</p>
                </div>
                <DiscussionAreaEditor value={areaGeoJson} highlightColor={highlightColor} onChange={setAreaGeoJson} />
                {areaStates.length > 0 && <p className="field-help">States in area: {areaStates.join(', ')}</p>}
                {areaWarnings.map((warning) => <div key={warning} className="alert alert-error">{warning}</div>)}
              </div>

              <div className="form-grid two-column">
                <div className="form-group">
                  <label className="form-label" htmlFor="tags">Tags</label>
                  <input id="tags" className="form-input" value={tags} onChange={(event) => setTags(event.target.value)} placeholder="severe setup, evening shift, watch coordination" />
                  <p className="field-help">Comma-separated staff tags for workflow and filtering.</p>
                </div>

                <div className="form-group">
                  <span className="form-label">Hazard types</span>
                  <div className="chip-grid">
                    {HAZARD_OPTIONS.map((hazard) => (
                      <button
                        key={hazard}
                        type="button"
                        className={`chip ${hazardTags.includes(hazard) ? 'chip-active' : ''}`}
                        onClick={() => toggleHazard(hazard)}
                      >
                        {hazard}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="action-row">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : status === 'published' ? 'Publish discussion' : status === 'ready' ? 'Save as ready' : status === 'in_review' ? 'Send to review' : 'Save draft'}
                </button>
              </div>
            </form>
        </div>
      </div>
    </div>
  );
};

export default ComposeDiscussionPage;
